<script setup>
import { onMounted, ref, watch } from 'vue'
import { getFullNameByUser, searchUsers } from '@/services/userService.js'

import TranslationProvider from '@/provider/translations.js'
import { useFiltersStore } from '@/services/store.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faFilter } from '@digitalpa/pro-solid-svg-icons'

library.add(faFilter)

const props = defineProps({
  isLoading: Boolean,
  currentFilters: {
    default: '',
    type: String
  }
})

const filtersStore = useFiltersStore()

const userListCC = ref([])
const userListDT = ref([])
const userCC = ref(null)
const userDT = ref(null)
const currentCCSearch = ref('')
const currentDTSearch = ref('')

onMounted(async () => {
  await setFilters()
})

const setFilters = async () => {
  const filters = filtersStore.filters

  if (filters['gec_involved_subject_main_contractor_user']) {
    userListCC.value = [filters['gec_involved_subject_main_contractor_user']]
  }
  if (filters['gec_involved_subject_construction_site_manager_user']) {
    userListDT.value = [filters['gec_involved_subject_construction_site_manager_user']]
  }
}

const resetFilters = () => {
  userCC.value = null
  userDT.value = null
}

async function fetchUsers(fullSearch, mainContractor) {
  if (fullSearch.length < 3) {
    return
  }

  try {
    const response = await searchUsers('cmb', {
      fullSearch: fullSearch,
      page: 1,
      limit: 20,
      excludeData: 0
    })

    if (mainContractor) {
      userListCC.value = response.data
    } else {
      userListDT.value = response.data
    }
  } catch (error) {
  }
}

watch(userCC, (newVal) => {
  if (!newVal) {
    filtersStore.filters.gec_involved_subject_main_contractor = null
    filtersStore.filters.gec_involved_subject_main_contractor_user = null
  }
})

watch(userDT, (newVal) => {
  if (!newVal) {
    filtersStore.filters.gec_involved_subject_construction_site_manager = null
    filtersStore.filters.gec_involved_subject_construction_site_manager_user = null
  }
})

defineExpose({
  resetFilters
})
</script>

<template>

  <div class="col-12 col-lg-3 px-2">
    <div class="mb-2">
      <label for="codice_risorsa_standard" class="form-label">
        {{ TranslationProvider.getTrans('app.codice_risorsa_standard') }}
      </label>
      <input
        id="codice_risorsa_standard"
        v-model="filtersStore.filters.gec_standard_resource_code"
        type="text"
        class="form-control form-control-sm"
        :placeholder="TranslationProvider.getTrans('app.codice_risorsa_standard')"
      />
    </div>
  </div>

  <div class="col-12 col-lg-3 px-2">
    <div class="mb-2">
      <label for="descrizione_risorsa_standard" class="form-label">
        {{ TranslationProvider.getTrans('app.descrizione_risorsa_standard') }}
      </label>
      <input
        id="descrizione_risorsa_standard"
        v-model="filtersStore.filters.gec_standard_resource_description"
        type="text"
        class="form-control form-control-sm"
        :placeholder="TranslationProvider.getTrans('app.descrizione_risorsa_standard')"
      />
    </div>
  </div>

  <div class="col-12 col-lg-3 px-2">
    <div class="mb-2">
      <label for="main_contractor" class="form-label">
        {{ TranslationProvider.getTrans('app.capo_commessa') }}
      </label>
      <v-select
        id="main_contractor"
        @search="(searchQuery, loading) => {
                currentCCSearch = searchQuery
                loading(true);
                fetchUsers(searchQuery, true).then(() => {
                  loading(false);
                });
              }"
        v-model="userCC"
        :options="userListCC"
        label="username"
        :filterable="false"
        @search:blur="() => {userListCC = []}"
        @search:selected="() => {userListCC = []}"
        :placeholder="TranslationProvider.getTrans('app.capo_commessa')"
        @option:selecting="(selectedOption) => {
              filtersStore.filters.gec_involved_subject_main_contractor_user = selectedOption
              filtersStore.filters.gec_involved_subject_main_contractor = selectedOption.reference
            }"
        clearable
      >
        <template v-slot:option="option">
          {{
            getFullNameByUser(option)
          }}
        </template>
        <template v-slot:selected-option="option">
          {{
            getFullNameByUser(option)
          }}
        </template>
        <template v-slot:no-options>
              <span v-if="currentCCSearch.length >= 3">
                 {{ TranslationProvider.getTrans('app.no_result_found') }}
              </span>
          <span v-else>
                 {{ TranslationProvider.getTrans('app.digita_per_cercare') }}
              </span>
        </template>
      </v-select>
      <small class="form-text">
        {{ TranslationProvider.getTrans('app.select2_min_chars') }}
      </small>
    </div>
  </div>

  <div class="col-12 col-lg-3 px-2">
    <div class="mb-2">
      <label for="contractor_site_manager" class="form-label">
        {{ TranslationProvider.getTrans('app.direttore_tecnico') }}
      </label>
      <v-select
        id="contractor_site_manager"
        @search="(searchQuery, loading) => {
              currentDTSearch = searchQuery
                loading(true);
                fetchUsers(searchQuery, false).then(() => {
                  loading(false);
                });
              }"
        v-model="userDT"
        :options="userListDT"
        label="username"
        @search:blur="() => {userListDT = []}"
        @search:selected="() => {userListDT = []}"
        :filterable="false"
        :placeholder="TranslationProvider.getTrans('app.direttore_tecnico', 'Direttore tecnico')"
        @option:selecting="(selectedOption) => {
              filtersStore.filters.gec_involved_subject_construction_site_manager_user = selectedOption;
              filtersStore.filters.gec_involved_subject_construction_site_manager = selectedOption.reference;
            }"
        clearable
      >
        <template v-slot:option="option">
          {{
            getFullNameByUser(option)
          }}
        </template>
        <template v-slot:selected-option="option">
          {{
            getFullNameByUser(option)
          }}
        </template>
        <template v-slot:no-options>
              <span v-if="currentCCSearch.length >= 3">
                 {{ TranslationProvider.getTrans('app.no_result_found') }}
              </span>
          <span v-else>
                 {{ TranslationProvider.getTrans('app.digita_per_cercare') }}
              </span>
        </template>
      </v-select>
      <small class="form-text">
        {{ TranslationProvider.getTrans('app.select2_min_chars') }}
      </small>
    </div>
  </div>
</template>

<style scoped></style>
